import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioLines, Loader2, Play, Plus, Search, Square, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SFX_CATEGORIES, SfxPlacementError } from '@/lib/ai-assistant/editor/soundEffects';
import type { SoundEffectRow } from '@/queries/soundEffects';
import { cn } from '@/lib/utils';
import { useSoundEffectsBrowse } from './useSoundEffectsBrowse';

export interface SoundEffectsBrowseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (sound: SoundEffectRow) => void;
  onUploadInstead: () => void;
  onPreviewStart?: () => void;
}

export function SoundEffectsBrowseDialog({ open, onOpenChange, onPick, onUploadInstead, onPreviewStart }: SoundEffectsBrowseDialogProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en' : 'pt';
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [region, setRegion] = useState<'br' | 'us' | ''>('');
  const [playing, setPlaying] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState(false);
  const [placementError, setPlacementError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previewSequence = useRef(0);
  const results = useSoundEffectsBrowse({ open, search, category, region, locale });

  const stopPreview = useCallback(() => {
    previewSequence.current += 1;
    const audio = audioRef.current;
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    setPlaying(null);
  }, []);

  useEffect(() => {
    stopPreview();
    setPreviewError(false);
    setPlacementError(null);
    return stopPreview;
  }, [open, search, category, region, locale, stopPreview]);

  const preview = (sound: SoundEffectRow) => {
    const wasPlaying = playing === sound.id;
    stopPreview();
    setPreviewError(false);
    if (wasPlaying) return;
    onPreviewStart?.();
    const audio = audioRef.current ?? new Audio();
    audioRef.current = audio;
    const request = previewSequence.current;
    audio.volume = 0.6;
    audio.src = sound.url;
    const failed = () => {
      if (request !== previewSequence.current) return;
      stopPreview();
      setPreviewError(true);
    };
    audio.onended = () => { if (request === previewSequence.current) stopPreview(); };
    audio.onerror = failed;
    setPlaying(sound.id);
    void audio.play().catch(failed);
  };

  const pick = (sound: SoundEffectRow) => {
    stopPreview();
    setPlacementError(null);
    try {
      onPick(sound);
      onOpenChange(false);
    } catch (error) {
      const code = error instanceof SfxPlacementError ? error.code : 'generic';
      setPlacementError(['outside_clip', 'invalid_time', 'too_many_sfx', 'too_many_overlapping_sfx'].includes(code) ? code : 'generic');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) stopPreview(); onOpenChange(next); }}>
      <DialogContent className="flex max-h-[85dvh] w-[calc(100%-2rem)] max-w-2xl flex-col overflow-hidden p-0" data-testid="sound-effects-picker">
        <DialogHeader className="border-b px-5 pb-4 pt-5 text-left">
          <div className="flex items-center gap-3 pr-7">
            <div className="rounded-lg bg-primary/10 p-2 text-primary"><AudioLines className="h-5 w-5" aria-hidden="true" /></div>
            <div><DialogTitle>{t('soundEffectsPicker.title')}</DialogTitle><DialogDescription className="mt-1">{t('soundEffectsPicker.description')}</DialogDescription></div>
          </div>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 pb-5">
          <div className="flex flex-col gap-2 pt-4 sm:flex-row">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input autoFocus value={search} onChange={event => setSearch(event.target.value)} aria-label={t('soundEffectsPicker.search')} placeholder={t('soundEffectsPicker.search')} className="pl-9" />
            </div>
            <Select value={category || 'all'} onValueChange={value => setCategory(value === 'all' ? '' : value)}>
              <SelectTrigger className="sm:w-44" aria-label={t('soundEffectsPicker.category')}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('soundEffectsPicker.allCategories')}</SelectItem>
                {SFX_CATEGORIES.map(value => <SelectItem key={value} value={value}>{t(`soundEffectsPicker.categories.${value}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2" role="group" aria-label={t('soundEffectsPicker.region')}>
            {(['', 'br', 'us'] as const).map(value => <Button key={value} type="button" size="sm" variant={region === value ? 'secondary' : 'ghost'} aria-pressed={region === value} onClick={() => setRegion(value)} className={cn('h-8 rounded-full px-3 text-xs', region === value && 'text-primary')}>{t(`soundEffectsPicker.regions.${value || 'all'}`)}</Button>)}
          </div>
          {(previewError || placementError) && <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{t(placementError ? `soundEffectsPicker.errors.${placementError}` : 'soundEffectsPicker.previewError')}</p>}
          <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border bg-muted/10" aria-busy={results.loading}>
            {results.items.length > 0 && <ul className="divide-y">
              {results.items.map(sound => <li key={sound.id} className="flex min-w-0 items-center gap-3 p-3">
                <Button type="button" size="icon" variant={playing === sound.id ? 'secondary' : 'outline'} className="h-9 w-9 shrink-0 rounded-full" onClick={() => preview(sound)} aria-label={t(`soundEffectsPicker.${playing === sound.id ? 'stop' : 'preview'}`, { name: sound.name })}>
                  {playing === sound.id ? <Square className="h-3.5 w-3.5" aria-hidden="true" /> : <Play className="h-3.5 w-3.5" aria-hidden="true" />}
                </Button>
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium" title={sound.name}>{sound.name}</p><p className="text-xs text-muted-foreground">{t(`soundEffectsPicker.categories.${sound.category}`)} · {new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(sound.duration)} s</p></div>
                <Button type="button" size="sm" variant="ghost" className="shrink-0" onClick={() => pick(sound)} aria-label={t('soundEffectsPicker.add', { name: sound.name })}><Plus className="h-4 w-4 sm:mr-1" aria-hidden="true" /><span className="hidden sm:inline">{t('soundEffectsPicker.addButton')}</span></Button>
              </li>)}
            </ul>}
            {results.loading && <div role="status" className="flex min-h-24 items-center justify-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />{t('soundEffectsPicker.loading')}</div>}
            {results.error && <div role="alert" className="p-6 text-center"><p className="mb-3 text-sm text-muted-foreground">{t('soundEffectsPicker.loadError')}</p><Button type="button" size="sm" variant="outline" onClick={() => void results.retry()}>{t('soundEffectsPicker.retry')}</Button></div>}
            {!results.loading && !results.error && !results.items.length && <p className="p-8 text-center text-sm text-muted-foreground">{t('soundEffectsPicker.empty')}</p>}
            {results.hasMore && !results.error && !results.loading && <div className="border-t p-3 text-center"><Button type="button" variant="ghost" size="sm" onClick={() => void results.loadMore()}>{t('soundEffectsPicker.loadMore')}</Button></div>}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
            <p className="text-xs text-muted-foreground" aria-live="polite">{t('soundEffectsPicker.count', { shown: results.items.length, total: results.total })}</p>
            <Button type="button" size="sm" variant="outline" onClick={() => { stopPreview(); onOpenChange(false); onUploadInstead(); }}><Upload className="mr-2 h-4 w-4" aria-hidden="true" />{t('soundEffectsPicker.upload')}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
